import { useCallback } from 'react'
import { User } from 'lucide-react'
import { Button, Tooltip } from '@highlight/ui'
import { useOpenViewInSidepanel } from '@renderer/overlay/hooks/use-open-view-in-sidepanel'
import { useSetSidepanelState, useSidepanelState } from '@renderer/overlay/atoms'
import { trackSidepanelOpened } from '@renderer/overlay/utils/track-sidepanel'
import { useSetSidepanelHotkeyOpened } from '@renderer/overlay/Sidepanel/atoms'

interface ForYouChipProps {
  count: number
}

export function ForYouChip({ count }: ForYouChipProps) {
  const { openViewInSidepanel } = useOpenViewInSidepanel()
  const sidepanelState = useSidepanelState()
  const setSidepanelState = useSetSidepanelState()
  const setHotkeyOpened = useSetSidepanelHotkeyOpened()

  const handleClick = useCallback(() => {
    if (sidepanelState !== 'open') {
      trackSidepanelOpened('mini')
      setHotkeyOpened(true)
    }
    // With nothing unread, open the sidebar without navigating it
    if (count === 0) setSidepanelState('open')
    else openViewInSidepanel('forYou', { initialFilter: 'staged' }, { sourceLabel: 'Mini' })
  }, [count, openViewInSidepanel, setHotkeyOpened, setSidepanelState, sidepanelState])

  const label = count === 0 ? 'Open sidebar' : count === 1 ? '1 unread in For You' : `${count} unread in For You`

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="ghost"
            size="md"
            aria-label={label}
            className="relative gap-1 px-1.5"
            onClick={(event) => {
              event.stopPropagation()
              handleClick()
            }}
          />
        }
      >
        <User aria-hidden="true" className="size-4" />
        {count > 0 && (
          <div className="bg-danger absolute bottom-1.5 right-1.5 size-1.5 rounded-full ring-2 ring-black" />
        )}
      </Tooltip.Trigger>
      <Tooltip.Content sideOffset={14}>{label}</Tooltip.Content>
    </Tooltip>
  )
}
